import { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import LoadRateForm from 'components/forms/load-rate-form/load-rate-form.component'
import { Tab } from 'types'
import LoadCarrierForm from 'pages/carrier-trip/load-confirmation/load-carrier-form.component'
import { MyCompanyTab, NotesTab } from './LoadConfirmationFormTabs'
import { LoadConfirmationPickupForm } from './LoadConfirmationPickupForm'
import { LoadConfirmationDeliveryForm } from './LoadConfirmationDeliveryForm'
import { LoadConfirmationFormValues } from '../schema'
import {
  loadConfirmationFormPanelSx,
  loadConfirmationTabContentSx,
} from './load-confirmation-form.styles'

const STATIC_TAB_COUNT = 3

type CarrierSuggestion = {
  id: string
  companyId: string
  companyName: string
  dispatchName: string
}

type LoadConfirmationFormPanelProps = {
  carrierSuggestions: CarrierSuggestion[]
  carrierSuggestionsLoading?: boolean
  skipCarrierAutoFill?: boolean
  hasTransportStops: boolean
  onAddPickupDelivery: () => void
  onChangePickupDelivery: () => void
  initialPickupTab?: boolean
}

export function LoadConfirmationFormPanel({
  carrierSuggestions,
  carrierSuggestionsLoading = false,
  skipCarrierAutoFill = false,
  hasTransportStops,
  onAddPickupDelivery,
  onChangePickupDelivery,
  initialPickupTab = false,
}: LoadConfirmationFormPanelProps) {
  const [tabsValue, setTabsValue] = useState(() =>
    initialPickupTab ? STATIC_TAB_COUNT : 0
  )
  const { control } = useFormContext<LoadConfirmationFormValues>()

  const watchedPickups = useWatch({ control, name: 'pickups' })
  const watchedDeliveries = useWatch({ control, name: 'deliveries' })
  const pickups = useMemo(() => watchedPickups ?? [], [watchedPickups])
  const deliveries = useMemo(() => watchedDeliveries ?? [], [watchedDeliveries])
  const pickupCount = Math.max(pickups.length, 1)
  const deliveryCount = Math.max(deliveries.length, 1)

  const dynamicTabs = useMemo(() => {
    const tabs: Tab[] = [
      { id: 'my-company', label: 'My Company' },
      { id: 'carrier', label: 'Carrier' },
      { id: 'rate', label: 'Rate' },
    ]

    if (pickups.length === 0) {
      tabs.push({ id: 'pickup-0', label: 'Pickup' })
    } else {
      pickups.forEach((_, idx) => {
        tabs.push({
          id: `pickup-${idx}`,
          label: pickups.length === 1 ? 'Pickup' : `Pickup ${idx + 1}`,
        })
      })
    }

    if (deliveries.length === 0) {
      tabs.push({ id: 'delivery-0', label: 'Delivery' })
    } else {
      deliveries.forEach((_, idx) => {
        tabs.push({
          id: `delivery-${idx}`,
          label: deliveries.length === 1 ? 'Delivery' : `Delivery ${idx + 1}`,
        })
      })
    }

    tabs.push({ id: 'notes', label: 'Note' })
    return tabs
  }, [pickups, deliveries])

  const notesTabIndex = STATIC_TAB_COUNT + pickupCount + deliveryCount
  const isNotesTab = tabsValue === notesTabIndex
  const pickupTabIndex = STATIC_TAB_COUNT

  useEffect(() => {
    if (tabsValue >= dynamicTabs.length) {
      setTabsValue(Math.max(0, dynamicTabs.length - 1))
    }
  }, [dynamicTabs.length, tabsValue])

  useEffect(() => {
    if (initialPickupTab) {
      setTabsValue(pickupTabIndex)
    }
  }, [initialPickupTab, pickupTabIndex])

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
  }

  const pickupIndices =
    pickups.length === 0 ? [0] : pickups.map((_, idx) => idx)
  const deliveryIndices =
    deliveries.length === 0 ? [0] : deliveries.map((_, idx) => idx)

  return (
    <Box sx={loadConfirmationFormPanelSx}>
      <BasicTabs
        tabs={dynamicTabs}
        value={tabsValue}
        handleChange={handleChange}
        scrollableContent={!isNotesTab}
      >
        <Box
          sx={{
            ...loadConfirmationTabContentSx,
            ...(isNotesTab
              ? {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : {}),
          }}
        >
          <TabPanel value={tabsValue} index={0}>
            <MyCompanyTab />
          </TabPanel>
          <TabPanel value={tabsValue} index={1}>
            <LoadCarrierForm
              carrierSuggestions={carrierSuggestions}
              carrierSuggestionsLoading={carrierSuggestionsLoading}
              skipAutoFill={skipCarrierAutoFill}
              compact
            />
          </TabPanel>
          <TabPanel value={tabsValue} index={2}>
            <LoadRateForm compact />
          </TabPanel>

          {pickupIndices.map((idx, tabOffset) => {
            const tabIndex = STATIC_TAB_COUNT + tabOffset
            return (
              <TabPanel key={`pickup-${idx}`} value={tabsValue} index={tabIndex}>
                <LoadConfirmationPickupForm
                  stopIndex={idx}
                  hasTransportStops={hasTransportStops}
                  onAddPickupDelivery={onAddPickupDelivery}
                  onChangePickupDelivery={onChangePickupDelivery}
                />
              </TabPanel>
            )
          })}

          {deliveryIndices.map((idx, tabOffset) => {
            const tabIndex = STATIC_TAB_COUNT + pickupCount + tabOffset
            return (
              <TabPanel key={`delivery-${idx}`} value={tabsValue} index={tabIndex}>
                <LoadConfirmationDeliveryForm
                  stopIndex={idx}
                  hasTransportStops={hasTransportStops}
                  onChangePickupDelivery={onChangePickupDelivery}
                />
              </TabPanel>
            )
          })}

          <TabPanel value={tabsValue} index={notesTabIndex}>
            <NotesTab fillHeight />
          </TabPanel>
        </Box>
      </BasicTabs>
    </Box>
  )
}
